import { editName, confirmAction, moveToFolder, vaultDialog } from './dialogs.js';
import { copyMap } from '../../../../engine/services/map-copy.js';
import { storageOf } from '../../../../engine/core/vault-folders.js';
import { showToast } from '../toast.js';
import { escapeHtml, escapeAttr } from '../../../../engine/core/validation.js';
import { updateDocumentSizes } from '../storage-usage.js';
import { t } from '../../../../shared/i18n/index.js';
/**
 * Vault Actions Module
 * CRUD operations for docs, folders, and shares
 */

import * as State from './state.js';

import { rotateShareDEK, removeAllMembers } from "../../../../engine/sync/share-rotation.js";
import { showManageShareModal } from "../share-modal.js";
import { moveMapOnline, moveMapToDevice } from "../map-storage.js";
import { exportAllData, importAllData, readExportFile } from "../../../../engine/crypto/data-export.js";
import { downloadExport, showExportWarnings } from "../export-flow.js";
import { chooseExportOptions } from '../export-options.js';
import { performShareRevocation } from "../../../../engine/sync/share-revocation.js";
import { engine } from "../../../../engine/index.js";
import { deleteWaitingFiles } from "../../../../engine/services/waiting-attachments.js";

// ============================================
// Dependencies (injected via init)
// ============================================

let _deps = {
  render: null,
  close: null,
  getFolderPathString: null,
};

/**
 * Initialize actions module with dependencies
 * @param {Object} deps - Dependencies
 * @param {Function} deps.render - Render function
 * @param {Function} deps.close - Close vault function
 * @param {Function} deps.getFolderPathString - Folder path string getter
 */
export function initActions(deps) {
  if (deps.render) _deps.render = deps.render;
  if (deps.close) _deps.close = deps.close;
  if (deps.getFolderPathString) _deps.getFolderPathString = deps.getFolderPathString;
}

// ============================================
// Owned Shares Fetch
// ============================================

/**
 * Fetch shares from the server and update local state
 * - Updates owned shares cache for "Shared with others" display
 * - Updates roles in VaultStorage for "Shared with me" items (in case of promotion/demotion)
 */
export async function fetchOwnedShares() {
  const VaultStorage = State.getVaultStorage();

  try {
    const response = await fetch("/api/shares", {
      credentials: "include",
    });

    if (!response.ok) {
      console.warn("[VaultUI] Failed to fetch shares");
      return [];
    }

    const shares = await response.json();

    const getRole = (s) => s.role;

    // Filter to only owned shares that are actually shared (have recipients).
    // Personal docs registered via POST /api/docs have member_count=1 (owner only)
    // and should not appear in the shared section.
    const ownedShareIds = shares
      .filter((s) => getRole(s) === "owner" && (s.member_count || 0) > 1)
      .map((s) => s.doc_id || s.id);

    // Enrich with local titles from docs[]
    // Only show shares that have a local vault entry. Shares without one
    // are not auto-deleted or auto-recovered — DB cleanup handles those.
    const index = VaultStorage.getIndex();
    const ownedShares = [];

    for (const id of ownedShareIds) {
      const localDoc = index?.docs?.find(d => d.docId === id);
      if (localDoc) {
        ownedShares.push({
          id,
          title: localDoc.title || "",
          created_at: localDoc.createdAt ? new Date(localDoc.createdAt).toISOString() : null,
          role: "owner",
        });
      }
    }

    State.setOwnedSharesCache(ownedShares);
    State.setOwnedSharesFetched(true);

    // Accepted maps that never reached the vault (failed accept on an older
    // client) — adopt them if their key authority verifies.
    const { adoptStrandedMemberships } = await import("../notifications.js");
    await adoptStrandedMemberships(shares).catch((err) =>
      console.warn("[VaultUI] Stranded membership check failed:", err));

    // Update roles for shared-with-me items in VaultStorage (server is authoritative)
    const sharedWithMe = shares.filter((s) => getRole(s) !== "owner");
    for (const serverShare of sharedWithMe) {
      const shareId = serverShare.doc_id || serverShare.id;
      const serverRole = getRole(serverShare);
      const localDoc = VaultStorage.getDoc?.(shareId);
      if (localDoc && localDoc.role !== serverRole) {
        console.log("[VaultUI] Role changed for doc", shareId, ":", localDoc.role, "->", serverRole);
        await VaultStorage.setDocEntry({ ...localDoc, role: serverRole });
      }
    }

    return ownedShares;
  } catch (error) {
    console.error("[VaultUI] Error fetching shares:", error);
    return [];
  }
}

// ============================================
// Shared Docs Validation
// ============================================

/**
 * Validate shared docs against the server and clean up revoked ones.
 * This runs in the background when the vault opens.
 * @returns {Promise<boolean>} True if any shares were cleaned up
 */
export async function validateSharedDocs() {
  const VaultStorage = State.getVaultStorage();

  // Copy the array to avoid issues if the original is modified during iteration
  const index = VaultStorage.getIndex();
  // (A map open by its link is not a membership: its socket's ticket says
  // whether the link still works.)
  const sharedDocs = [...((index?.docs || []).filter(d => d.role && d.role !== "owner" && !d.link))];
  if (sharedDocs.length === 0) return false;

  console.log("[VaultUI] Validating", sharedDocs.length, "shared docs");

  let hasChanges = false;

  for (const share of sharedDocs) {
    try {
      const response = await fetch(`/api/shares/${share.docId}`, {
        credentials: "include",
      });

      if (response.status === 403 || response.status === 404) {
        // Share has been revoked or deleted — trigger recovery as safety net.
        // The WS/notification paths may have already handled this (performShareRevocation
        // deduplicates), but if they didn't (e.g. notification dismissed), this catches it.
        const shareInfo = VaultStorage.getDoc?.(share.docId);
        const title = shareInfo?.title || t('vault.recoveredMapName');
        console.log("[VaultUI] Share revoked, triggering recovery:", share.docId);
        performShareRevocation(share.docId, title, { fromVaultValidation: true }).then(() => {
          if (_deps.render) _deps.render();
        });
        hasChanges = true;
      } else if (!response.ok) {
        // Transient server error (500, 503, etc.) - skip, don't trigger recovery
        console.warn("[VaultUI] Transient error validating share, skipping:", share.docId, response.status);
      }
    } catch (error) {
      console.warn("[VaultUI] Error validating share:", share.docId, error);
    }
  }

  if (hasChanges) {
    console.log("[VaultUI] Cleaned up revoked shares");
  }

  return hasChanges;
}

// ============================================
// Export/Import
// ============================================

/**
 * Handle data export
 */
let exporting = false, importing = false;
export async function handleExportData() {
  if (exporting) return;
  exporting = true;
  try {
    const options = await chooseExportOptions();
    if (!options) return;
    const { exportData, exportErrors } = await exportAllData(options);
    await downloadExport(exportData, null, options);
    if (!options.includeAttachments && exportErrors.length) showExportWarnings(exportErrors);
  } catch (error) { showToast(t('vault.export.failed', { error: error.message }), { type: 'error' }); }
  finally { exporting = false; }
}

export async function handleImportData(file) {
  if (!file || importing) return;
  importing = true;
  try {
    const data = await readExportFile(file);
    if (!Array.isArray(data.documents)) throw new Error(t('vault.import.chooseBackup'));
    const counts = { maps: t('vault.import.mapCount', { count: data.documents.length }),
      folders: t('vault.import.folderCount', { count: data.folders?.length || 0 }) };
    return await vaultDialog({ title: t('vault.data.import'), submit: t('vault.import.maps'),
      content: `<p>${escapeHtml(t('vault.import.question', counts))}</p><p>${escapeHtml(t('vault.import.keepExisting'))}</p>`,
      onSubmit: async () => {
        const result = await importAllData(data, { mergeWithExisting: true, keepCurrentDocument: true });
        _deps.render?.();
        const errors = [...result.errors, ...result.syncErrors];
        if (errors.length) showExportWarnings(errors.map(error => ({ name: t('common.import'), error })));
        showToast(`${t('vault.import.importedCount', { count: result.imported })} · ${t('vault.import.skippedCount', { count: result.skipped })}`, { type: errors.length ? 'warning' : 'success' });
      } });
  } catch (error) { showToast(t('vault.import.failed', { error: error.message }), { type: 'error' }); }
  finally { importing = false; }
}

export function chooseVaultImport() {
  const input = document.createElement('input');
  input.type = 'file'; input.accept = '.json,.zip';
  input.addEventListener('change', () => handleImportData(input.files?.[0]), { once: true });
  input.click();
}

// ============================================
// Action Handlers
// ============================================

/**
 * Handle folder actions (new-subfolder, rename, delete)
 * @param {Element} target - Click target element
 * @param {string} folderId - Folder ID
 */
export function handleFolderAction(target, folderId) {
  const action = target.closest("[data-action]")?.dataset.action;

  if (action === "new-subfolder") {
    promptNewFolder(folderId);
  } else if (action === "move") {
    const vault = State.getVaultStorage(), entry = vault.getIndex().folders.find(f => f.id === folderId);
    if (entry) moveToFolder({ vault, entry, isFolder: true, onMoved: () => _deps.render?.() });
  } else if (action === "rename") {
    promptRenameFolder(folderId);
  } else if (action === "delete") {
    confirmDeleteFolder(folderId);
  }
}

/**
 * Handle map actions (rename, move, clone, delete, promote, move-to-device, save-local)
 * @param {Element} target - Click target element
 * @param {string} docId - Document ID
 */
export function handleMapAction(target, docId) {
  const action = target.closest("[data-action]")?.dataset.action;

  if (action === "rename") {
    promptRenameMap(docId);
  } else if (action === "move") {
    showMoveModal(docId);
  } else if (action === "export") {
    exportMap(docId);
  } else if (action === "details") {
    showMapDetails(docId);
  } else if (action === "history") {
    void import("../history.js").then((History) => History.open(docId));
  } else if (action === "clone") {
    cloneDoc(docId);
  } else if (action === "delete") {
    confirmDeleteMap(docId);
  } else if (action === "promote") {
    // Phase 5d: move a local doc to the online slot.
    confirmPromoteToOnline(docId);
  } else if (action === "move-to-device") {
    void moveMapToDevice(docId).then((moved) => { if (moved) _deps.render?.(); });
  } else if (action === "save-local") {
    // Phase 5d: duplicate an online doc as a new local-only doc.
    confirmDuplicateAsLocal(docId);
  }
}

/**
 * Move a map on this device to the online vault (interfaces/classic/ui/map-storage.js: the same
 * confirmation as the header's map indicator; an open map is reopened online).
 */
async function confirmPromoteToOnline(docId) {
  if (await moveMapOnline(docId) && _deps.render) _deps.render();
}

/**
 * Phase 5d — confirm + run duplicateAsLocal. Online doc → new local
 * doc (non-destructive: the source online doc is untouched).
 */
async function confirmDuplicateAsLocal(docId) {
  const VaultStorage = State.getVaultStorage();
  const doc = VaultStorage.getIndex()?.docs?.find((d) => d.docId === docId);
  if (!doc) return;
  const confirmed = await confirmAction(t('vault.saveLocal.confirm', { title: doc.title }));
  if (!confirmed) return;

  try {
    const result = await VaultStorage.duplicateAsLocal(docId);
    if (!result.ok) {
      showToast(t('vault.saveLocal.failed', { error: result.error }));
      return;
    }
    if (_deps.render) _deps.render();
  } catch (err) {
    console.error("[VaultUI] duplicateAsLocal failed:", err);
    showToast(t('vault.error.unexpected', { error: err.message || err }));
  }
}

/**
 * Handle shared map actions (open, leave)
 * @param {Element} target - Click target element
 * @param {string} shareId - Share ID
 */
export function handleSharedAction(target, shareId) {
  const action = target.closest("[data-action]")?.dataset.action;

  if (action === "open-share") {
    openDoc(shareId);
  } else if (action === "leave-share") {
    confirmLeaveShare(shareId);
  }
}

/**
 * Handle owned share actions (manage, unshare)
 * @param {Element} target - Click target element
 * @param {string} shareId - Share ID
 */
export function handleOwnedShareAction(target, shareId) {
  const action = target.closest("[data-action]")?.dataset.action;

  if (action === "manage-share") {
    openManageShareModalAction(shareId);
  } else if (action === "unshare") {
    confirmUnshare(shareId);
  }
}

// ============================================
// Share Modal
// ============================================

/**
 * Open the manage share modal
 * @param {string} shareId - Share ID
 */
export async function openManageShareModalAction(shareId) {
  try {
    if (_deps.close) _deps.close(); // Close vault panel first
    const result = await showManageShareModal(shareId);
    if (result?.action === "unshared" && result.shareId) {
      // In the unified doc model, docId === shareId. Do NOT call deleteDoc —
      // that would DELETE /api/docs/:id, nuking the owner's CRDT data, blob
      // refs, the just-created revocation snapshots, and the owner's own
      // doc_keys row. "Stop sharing" should convert the doc back to a private
      // owned doc, not destroy it. (The unshare() inside the modal already
      // revoked all recipients via rotate+revoke.)
      State.setOwnedSharesCache(State.getOwnedSharesCache().filter((s) => s.id !== result.shareId));
      if (_deps.render) _deps.render();
    }
  } catch (error) {
    console.error("[VaultUI] Error opening manage share modal:", error);
    showToast(t('vault.share.openFailed'));
  }
}

/**
 * Confirm and stop sharing
 * @param {string} shareId - Share ID
 */
export async function confirmUnshare(shareId) {
  const ownedSharesCache = State.getOwnedSharesCache();
  const share = ownedSharesCache.find((s) => s.id === shareId);
  if (!share) return;

  const confirmed = await confirmAction(t('vault.unshare.confirm', { title: share.title }));
  if (!confirmed) return;

  try {
    // Fetch current permissions to get all non-owner user IDs
    const permsResp = await fetch(`/api/shares/${shareId}/permissions`, { credentials: "include" });
    if (!permsResp.ok) throw new Error("Failed to fetch permissions");
    const permissions = await permsResp.json();
    const nonOwnerIds = permissions
      .filter(p => p.role !== "owner")
      .map(p => p.user_id);

    if (nonOwnerIds.length > 0) {
      // Atomic rotate+revoke: server creates snapshot internally from current baseline
      await rotateShareDEK(shareId, { revokeUserIds: nonOwnerIds });
    }

    // In the unified doc model, docId === shareId. Do NOT delete the vault entry —
    // that would delete the owner's document. The owner keeps their doc; only
    // recipients lose access.

    // Remove from owned-shares cache (no longer "shared with others") and re-render
    State.setOwnedSharesCache(ownedSharesCache.filter((s) => s.id !== shareId));
    if (_deps.render) _deps.render();

    console.log("[VaultUI] Stopped sharing:", shareId);
  } catch (error) {
    console.error("[VaultUI] Error stopping share:", error);
    showToast(t('vault.unshare.failed'));
  }
}

// ============================================
// Shared Document Operations
// ============================================

/**
 * Open a shared document
 * @param {string} shareId - Share ID
 */
export async function openDoc(shareId) {
  const VaultStorage = State.getVaultStorage();
  const callbacks = State.getCallbacks();

  // Guard against double-clicks triggering multiple opens for the same doc
  if (State.getOpeningDocId() === shareId) {
    console.log(
      "[VaultUI] Already opening shared doc, ignoring duplicate:",
      shareId,
    );
    return;
  }

  console.log("[VaultUI] Opening shared doc:", shareId);
  State.setOpeningDocId(shareId);

  try {
    // Get share info from vault storage (unified docs[] or legacy sharedSubtrees[])
    const share = VaultStorage.getDoc?.(shareId);
    if (!share) {
      console.error("[VaultUI] Share not found:", shareId);
      return;
    }

    // Close vault panel
    if (_deps.close) _deps.close();

    // Notify the app to open this shared doc
    // The app will handle connecting to CRDT sync, etc.
    if (callbacks.onShareOpen) {
      await callbacks.onShareOpen(shareId, share);
    }
  } finally {
    State.setOpeningDocId(null);
  }
}

/**
 * Confirm and leave a share
 * @param {string} shareId - Share ID
 */
export async function confirmLeaveShare(shareId) {
  const VaultStorage = State.getVaultStorage();
  const callbacks = State.getCallbacks();

  const share = VaultStorage.getDoc?.(shareId);
  if (!share) return;

  // Open by its link, not a member: removed from this account's maps only.
  if (share.link) {
    if (!await confirmAction(t('share.link.holder.removeConfirm', { title: share.title }))) return;
    const wasCurrentDoc = VaultStorage.getCurrentDocId() === shareId;
    try {
      await engine.sharing.link.remove(shareId);
      _deps.render?.();
      if (wasCurrentDoc && callbacks.onDocCreate) {
        const { docId: newDocId } = await VaultStorage.createMap(t('vault.leave.newMapName'));
        await callbacks.onDocCreate(newDocId);
      }
    } catch (error) {
      console.error("[VaultUI] Error removing a map opened by link:", error);
      showToast(t('vault.leave.failed'));
    }
    return;
  }

  const confirmed = await confirmAction(t('vault.leave.confirm', { title: share.title }));
  if (!confirmed) return;

  try {
    // Check if this is the currently open doc
    const wasCurrentDoc = VaultStorage.getCurrentDocId() === shareId;

    const MapKeyTrust = await import('../../../../engine/crypto/map-key-trust.js');
    const { getCurrentUser } = await import('../../../../engine/auth/handlers.js');
    const authority = await MapKeyTrust.synchronize(shareId);
    let key_authority;
    if (authority) {
      const members = authority.members.filter(m => m.userId !== getCurrentUser().user_id);
      const dek = await VaultStorage.getDocDEK(shareId);
      try { key_authority = await MapKeyTrust.prepare(shareId, 'leave', members, dek, authority); }
      finally { dek.fill(0); }
    }
    // Call API to remove myself from the share
    const response = await fetch(`/api/shares/${shareId}/permissions/me`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "include",
      body: JSON.stringify({ key_authority }),
    });

    if (!response.ok) {
      throw new Error("Failed to leave share");
    }

    // Remove from local storage
    await VaultStorage.deleteDoc(shareId);

    // Re-render vault UI
    if (_deps.render) _deps.render();

    // If this was the current doc, create a new default doc
    if (wasCurrentDoc && callbacks.onDocCreate) {
      const { docId: newDocId } = await VaultStorage.createMap(t('vault.leave.newMapName'));
      await callbacks.onDocCreate(newDocId);
    }

    console.log("[VaultUI] Left share:", shareId);
  } catch (error) {
    console.error("[VaultUI] Error leaving share:", error);
    showToast(t('vault.leave.failed'));
  }
}

/**
 * Copy a shared map into a local folder
 * This creates a local snapshot of the shared doc
 * @param {string} shareId - Share ID
 * @param {string} folderId - Target folder ID
 */
export async function copySharedMapToFolder(shareId, folderId) {
  return cloneDoc(shareId, folderId);
}

// ============================================
// Folder Operations
// ============================================

/**
 * Toggle folder expansion
 * @param {string} folderId - Folder ID
 */
export function toggleFolder(folderId) {
  State.toggleExpandedFolder(folderId);
  if (_deps.render) _deps.render();
}

/**
 * Prompt for new folder name and create it
 * @param {string|null} parentId - Parent folder ID or null for root
 */
export async function promptNewFolder(parentId) {
  const vault = State.getVaultStorage();
  if (!vault.getIndex().folders.some(f => f.id === parentId)) parentId = null;
  return editName({ title: t('vault.folder.new'), submit: t('vault.folder.create'), onSubmit: async name => {
    const id = await vault.createFolder(name, parentId);
    if (parentId) State.addExpandedFolder(parentId);
    State.setSelectedFolderId(id);
    _deps.render?.();
    return id;
  } });
}

/**
 * Prompt for new folder name and rename
 * @param {string} folderId - Folder ID
 */
export async function promptRenameFolder(folderId) {
  const vault = State.getVaultStorage(), folder = vault.getIndex().folders.find(f => f.id === folderId);
  if (!folder) return;
  return editName({ title: t('vault.folder.rename'), value: folder.name, onSubmit: async name => {
    await vault.renameFolder(folderId, name); _deps.render?.();
  } });
}

/**
 * Confirm and delete folder
 * @param {string} folderId - Folder ID
 */
export async function confirmDeleteFolder(folderId) {
  const vault = State.getVaultStorage(), index = vault.getIndex();
  const folder = index.folders.find(f => f.id === folderId);
  if (!folder || folder.parentId === null) return;
  const parent = index.folders.find(f => f.id === folder.parentId);
  return vaultDialog({ title: t('vault.folder.delete'), submit: t('vault.folder.delete'), danger: true,
    content: `<p class="vault-dialog-message">${escapeHtml(t('vault.folder.deleteConfirm', { name: folder.name, parent: parent?.name || t('vault.folder.vaultRoot') }))}</p>`,
    onSubmit: async () => {
      await vault.deleteFolder(folderId, parent?.id);
      if (State.getSelectedFolderId() === folderId) State.setSelectedFolderId(parent?.id);
      _deps.render?.();
    } });
}

// ============================================
// Doc Operations
// ============================================

/**
 * Open a personal doc
 * @param {string} docId - Document ID
 */
export async function openPersonalDoc(docId) {
  const callbacks = State.getCallbacks();

  // Guard against double-clicks triggering multiple opens for the same doc
  if (State.getOpeningDocId() === docId) {
    console.log("[VaultUI] Already opening doc, ignoring duplicate:", docId);
    return;
  }

  console.log("[VaultUI] Opening doc:", docId);
  State.setOpeningDocId(docId);

  try {
    if (callbacks.onDocOpen) {
      console.log("[VaultUI] Calling onDocOpen callback");
      await callbacks.onDocOpen(docId);
    } else {
      console.warn("[VaultUI] No onDocOpen callback registered");
    }
    if (_deps.close) _deps.close();
  } finally {
    State.setOpeningDocId(null);
  }
}

/**
 * Prompt for new doc title and create in folder
 * @param {string} folderId - Folder ID
 */
export async function promptNewDocInFolder(folderId) {
  const vault = State.getVaultStorage();
  const folder = vault.getIndex().folders.find(f => f.id === folderId);
  return editName({ title: t('vault.map.new'), label: t('vault.map.titleLabel'), value: t('vault.map.defaultName'), submit: t('vault.map.create'), onSubmit: async title => {
    // Offline, an online map is kept on this device until the server can be reached.
    const { docId } = await vault.createMap(title, folder?.id || null, null, { ...(folder && { storage: storageOf(folder) }), whenOffline: 'device' });
    await State.getCallbacks().onDocCreate?.(docId);
    _deps.close?.();
  } });
}

/**
 * Clone/duplicate a document
 * @param {string} docId - Document ID
 */
export async function cloneDoc(docId, folderId) {
  const vault = State.getVaultStorage(), source = vault.getDoc(docId);
  if (!source) return;
  return editName({ title: t('vault.map.duplicate'), label: t('vault.map.copyTitleLabel'), value: t('vault.map.copyName', { title: source.title || t('common.untitled') }), submit: t('vault.map.duplicate'),
    onSubmit: async title => {
      const result = await copyMap(vault, docId, { title, folderId });
      _deps.render?.();
      if (result.errors.length || result.syncErrors.length) {
        showExportWarnings([...result.errors, ...result.syncErrors].map(error => ({ name: title, error })));
      } else showToast(t('vault.map.duplicated'), { type: 'success' });
    } });
}

/**
 * Prompt for new map title and rename
 * @param {string} docId - Document ID
 */
export async function promptRenameMap(docId) {
  const vault = State.getVaultStorage(), map = vault.getDoc(docId);
  if (!map) return;
  return editName({ title: t('vault.map.rename'), label: t('vault.map.titleLabel'), value: map.title, onSubmit: async title => {
    await vault.renameMap(docId, title);
    if (docId === vault.getCurrentDocId()) {
      const bridge = await import('../../../../engine/sync/crdt-bridge.js');
      const root = bridge.getTreeFromCRDT();
      if (root?.id) bridge.updateNode(root.id, { name: title });
      const label = document.getElementById('doc-title-text');
      if (label) label.textContent = title;
    }
    _deps.render?.();
  } });
}

/**
 * Confirm and delete map
 * @param {string} docId - Document ID
 */
export async function confirmDeleteMap(docId) {
  const vault = State.getVaultStorage(), map = vault.getDoc(docId);
  if (!map || (map.role && map.role !== 'owner')) return;
  return vaultDialog({ title: t('vault.map.delete'), submit: t('vault.map.delete'), danger: true,
    content: `<p class="vault-dialog-message">${escapeHtml(t('vault.map.deleteConfirm', { title: map.title }))}</p>
      ${storageOf(map) === 'online' ? `<p>${escapeHtml(t('vault.map.deleteSharedNote'))}</p>` : ''}`,
    onSubmit: async () => {
      // Check fresh permissions instead of relying on the background share cache.
      if (storageOf(map) === 'online') await removeAllMembers(docId);
      await vault.deleteDoc(docId);
      // Its files attached offline and not sent go with it.
      await deleteWaitingFiles([docId]).catch(() => {});
      State.setOwnedSharesCache(State.getOwnedSharesCache().filter(s => s.id !== docId));
      _deps.render?.();
      await State.getCallbacks().onDocDelete?.(docId);
    } });
}

/**
 * Show modal to move map to different folder
 * @param {string} docId - Document ID
 */
export async function showMoveModal(docId) {
  const vault = State.getVaultStorage(), entry = vault.getDoc(docId);
  if (!entry) return;
  return moveToFolder({ vault, entry, onMoved: folderId => {
    State.setSelectedFolderId(folderId); State.addExpandedFolder(folderId);
    _deps.render?.(); showToast(t('vault.map.moved'), { type: 'success' });
  } });
}

/**
 * Move a map to a different folder
 * @param {string} docId - Document ID
 * @param {string|null} folderId - Target folder ID
 */
export async function moveMap(docId, folderId) {
  try {
    await State.getVaultStorage().moveMap(docId, folderId);
    _deps.render?.(); showToast(t('vault.map.moved'), { type: 'success' });
  } catch (error) { showToast(error.message, { type: 'error' }); }
}

export async function exportMap(docId) {
  const options = await chooseExportOptions();
  if (!options) return;
  try {
    const { exportData, exportErrors } = await exportAllData({ ...options, docIds: [docId] });
    await downloadExport(exportData, null, options);
    if (exportErrors.length && !options.includeAttachments) showExportWarnings(exportErrors);
  } catch (error) { showToast(error.message, { type: 'error' }); }
}

export function showMapDetails(docId) {
  const vault = State.getVaultStorage(), doc = vault.getDoc(docId);
  if (!doc) return;
  return vaultDialog({ title: doc.title || t('vault.details.title'), submit: t('common.done'),
    content: `<dl class="doc-properties-info"><dt>${escapeHtml(t('vault.details.location'))}</dt><dd>${escapeHtml(_deps.getFolderPathString(doc.folderId, vault.getIndex()))}</dd>
      <dt>${escapeHtml(t('vault.details.storage'))}</dt><dd>${escapeHtml(t(storageOf(doc) === 'local' ? 'vault.details.onDevice' : 'vault.details.onlineVault'))}</dd>
      <dt>${escapeHtml(t('vault.details.size'))}</dt><dd data-storage-doc-id="${escapeAttr(docId)}" data-size-detail>${escapeHtml(t('vault.size.loading'))}</dd></dl>`,
    setup: modal => updateDocumentSizes(modal, vault), onSubmit: () => true });
}
